import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ControlSettingTemplatesService } from '../shared/services/control-setting-templates.service';
import { ToastService } from '@captiveaire/toast';
import { DropdownService, IDropdownData } from '@captiveaire/advanceddropdown';
import { QueryService } from '@captiveaire/query';
import {
    IControlSettingTemplatesSearchCriteria,
    ILocationSummary,
    IModuleSummary,
    IModuleSummarySearchCriteria,
} from '@caslink/common-client';
import { ModuleSummaryService } from '@caslink/common-client/ng-app/moduleSummary/ModuleSummaryService';
import { LocationSummaryService } from '@caslink/common-client/ng-app/locationSummary/LocationSummaryService';
import { DeviceHardwareItemQueryService } from '@caslink/common-client/ng-app/deviceHardwareItemQuery/DeviceHardwareItemQueryService';
import { Column, ITableItem, ITableConfig, TableService } from '@captiveaire/table';
import { ControlSettingTemplatesTreeViewCellComponent } from '../control-setting-templates-tree-view-cell/control-setting-templates-tree-view-cell.component';

interface TemplateChoiceDialogData {
    category: string;
    templateType: string;
    locationId: number;
}

@Component({
    selector: 'dl-template-choice-dialog',
    template: `
       <cl-sub-toolbar>
        <nav-toolbar [isBackNavDisabledOverride]="true"
                     currentTitleOverride="Select Template"></nav-toolbar>

            <div style="flex: 1 1 auto;"></div>
            <div>
            <button mat-button (click)="onCancelClick()">Cancel</button>
            <button mat-button [mat-dialog-close]="templateKey" [disabled]="templateKey== ''" >Ok</button>
            </div>
       </cl-sub-toolbar>

       <div mat-dialog-content>
        <div fxLayout="row" >
        <advanced-dropdown class="caslink"
                               [data]="locations"
                               [(model)]="query.locationId"
                               (change)="locationHandler()"
                               item-height="35"
                               width="350"></advanced-dropdown>

        <advanced-dropdown class="caslink"
                               [data]="modules"
                               [(model)]="query.moduleId"
                               (change)="moduleHandler()"
                               item-height="35"
                               width="350"></advanced-dropdown>

        <advanced-dropdown class="caslink"
                               [data]="devices"
                               [(model)]="query.deviceId"
                               (change)="deviceHandler()"
                               item-height="35"
                               width="350"></advanced-dropdown>

        </div>

                <cas-table [data]="data"
                           [config]="tableConfig"
                           (getData)="getData()"
                           (viewItem)="selTemplate($event)"
                           [disableAdd]="true"
                >
                </cas-table>



        </div>

    `,
    styles: [
        `
        div[mat-dialog-content] {
          padding: 20px;
        }

        `
    ]
})
export class TemplateChoiceDialogComponent {

    public static $name: string = 'templateChoiceDialog';
    public templateKey: string = '';


    public locationId: number;

    public query: IControlSettingTemplatesSearchCriteria;
    public data: CASLink.Ui.ViewModel.ControlSettingTemplates.ControlSettingTemplatesViewModel[];
    public tableConfig: ITableConfig;

    public locations: IDropdownData;
    public modules: IDropdownData;
    public devices: IDropdownData;
    public categories: IDropdownData;

    public selectedItem: ITableItem = null;




    constructor(
        private dialogRef: MatDialogRef<TemplateChoiceDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public dlgData: TemplateChoiceDialogData,
        private controlSettingTemplatesService: ControlSettingTemplatesService,
        public dropdownService: DropdownService,
        private locationSummaryService: LocationSummaryService,
        private moduleSummaryService: ModuleSummaryService,
        private deviceHardwareItemQueryService: DeviceHardwareItemQueryService,
        private queryService: QueryService,
        private toastService: ToastService,
        private tableService: TableService
    ) {
    }


    public ngOnInit() {
        this.locationId = this.dlgData.locationId;
        this.initGrid();
        this.initDropdowns();
        (this.devices.query as any).category = this.dlgData.category == '' ? null : this.dlgData.category; //we only show  devices of THIS category and it can not be empty or null, but we never send in empty to api so sanity check here
        this.getData();
    }

    private initDropdowns() {
        this.prepareLocationDropdown();
        this.prepareDeviceDropdown();
        this.prepareModuleDropdown();
    }

    private initGrid() {
        const loadLimit = 5;
        this.query = this.queryService.createQuery(loadLimit, 'transactionTimestamp', 1, false);
        this.query.includeTotalCount = true;
        this.query.hardwareCategory = this.dlgData.category;
        this.query.templateType = this.dlgData.templateType;
        this.query.wildcard = '';
        this.query.locationId = this.locationId === 0 ? null : this.locationId;

        this.tableConfig = {
            columns: this.getColumns(),
            query: this.query,
            showEditColumn: true,
        };

    }

    private getColumns(): any {
        const result = [];
        result.push(this.getColumn('Name', 'templateName'));
        result.push(this.getColumn('Created By', 'userName'));
        result.push(this.getColumn('Created', 'transactionTimestamp'));
        result.push(this.getColumn('Location', 'location'));
        result.push(this.getColumn('Module', 'module'));
        result.push(this.getColumn('Device', 'device'));
        result.push(this.getTemplateColumn());
        return result;
    }


    private getColumn(title: string, label: string, sortable: boolean = true): Column {
        const column = this.tableService.createColumn(title, label, sortable);
        column.smallerHeader = true;
        return column;
    }

    private getTemplateColumn(): Column {
        const column = this.tableService.createColumn('Template', 'template', false);
        column.component = ControlSettingTemplatesTreeViewCellComponent;
        column.smallerHeader = true;
        return column;
    }

    public selTemplate(item: ITableItem) {
        if (this.selectedItem != null) {
            this.selectedItem.$selected = false;
        }
        this.selectedItem = item;
        this.selectedItem.$selected = true;

        this.templateKey = (this.selectedItem as CASLink.Ui.ViewModel.ControlSettingTemplates.ControlSettingTemplatesViewModel).id as string;

    }


    private prepareLocationDropdown() {
        this.locations = this.dropdownService.prepareDropdown('Location',
            'description',
            this.locationSummaryService,
            this.locationId === undefined || this.locationId === 0 ? null : this.locationId.toString());

        this.locations.titleHandler = (item: ILocationSummary) => {
            return `${item.street1} ${item.street2} ${item.city} ${item.state} ${item.zip} ${item.country}`;
        };
    }

    private prepareModuleDropdown() {
        this.modules = this.dropdownService.prepareDropdown('Module',
            'description', this.moduleSummaryService);

        this.modules.titleHandler = (item: IModuleSummary) => {
            return `${item.mac}`;
        };

        // set default filter id
        (this.modules.query as IModuleSummarySearchCriteria).locationId = this.query.locationId;
    }


    private prepareDeviceDropdown() {
        this.devices = this.dropdownService.prepareDropdown('Device',
            'tag', this.deviceHardwareItemQueryService);
        (this.devices.query  as any /* as IHardwareItemSearchCriteria */).isDevice = true;
        (this.devices.query as any /* as IHardwareItemSearchCriteria */).locationId = this.query.locationId;
        (this.devices.query as any /* as IHardwareItemSearchCriteria */).moduleId = this.query.moduleId;
    }


    public onCancelClick() {
        this.dialogRef.close();
    }


    public locationHandler(searchless?: boolean) {
        (this.modules.query as IModuleSummarySearchCriteria).locationId = this.query.locationId;
        (this.devices.query as any /* as IHardwareItemSearchCriteria */).locationId = this.query.locationId;
        this.modules.search();

        if (this.query.locationId !== null) {
            this.query.moduleId = this.modules.deselectItem();
            this.moduleHandler(true);
        } else {
            this.devices.search();
        }

        if (!searchless) {
            this.getData();
        }
    }

    public moduleHandler(searchless?: boolean) {
        (this.devices.query as any /* as IHardwareItemSearchCriteria */).moduleId = this.query.moduleId;
        if (this.query.deviceId !== null) {
            this.query.deviceId = this.devices.deselectItem();
        }

        this.devices.search();
        if (!searchless) {
            this.getData();
        }
    }

    public deviceHandler() {
        this.getData();
    }

    public getData(): any {
        this.templateKey = '';
        return this.controlSettingTemplatesService.getControlSettingTemplatesList(this.query)
            .then(response => {


                this.data = response.records;
                this.query.totalObjects = response.totalRecordsCount;
            }).catch(this.toastService.errorResponse);
    }

}
